import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = [
  { value: 'general', label: 'General',    color: 'bg-blue-100 text-blue-800',    icon: 'fa-bullhorn' },
  { value: 'urgent',  label: 'Urgent',     color: 'bg-red-100 text-red-800',      icon: 'fa-exclamation-triangle' },
  { value: 'event',   label: 'Event',      color: 'bg-purple-100 text-purple-800', icon: 'fa-calendar-star' },
  { value: 'holiday', label: 'Holiday',    color: 'bg-green-100 text-green-800',   icon: 'fa-umbrella-beach' },
];

const catMeta = (key) => CATEGORIES.find(c => c.value === key) || CATEGORIES[0];


export default function Notifications() {
  const { user } = useAuth();
  const role = user?.role;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [filter, setFilter] = useState('all');

  const canPost = ['admin', 'superintendent', 'principal', 'teacher'].includes(role);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/announcements/');
      setItems(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    try {
      await api.post(`/announcements/${id}/read`);
      setItems(prev => prev.map(a => a.id === id ? { ...a, is_read: true } : a));
    } catch (err) {
      // silent
    }
  };

  const deleteAnnouncement = async (a) => {
    if (!window.confirm('Remove this announcement?')) return;
    try {
      await api.delete(`/announcements/${a.id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const scopeLabel = (a) => {
    if (a.scope === 'global') return { text: 'Everyone', icon: 'fa-globe', color: 'text-indigo-600' };
    if (a.scope === 'campus') return { text: 'Campus', icon: 'fa-building', color: 'text-blue-600' };
    if (a.scope === 'class')  return { text: 'Class',  icon: 'fa-chalkboard', color: 'text-green-600' };
    return { text: a.scope, icon: 'fa-info-circle', color: 'text-gray-500' };
  };

  const filtered = useMemo(() => {
    if (filter === 'unread') return items.filter(a => !a.is_read);
    if (filter === 'read')   return items.filter(a => a.is_read);
    return items;
  }, [items, filter]);

  const unreadCount = items.filter(a => !a.is_read).length;

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <i className="fas fa-bell text-yellow-500"></i>
            Notifications
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {unreadCount} new
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Announcements posted by admins, superintendents, principals, and teachers.
          </p>
        </div>

        <div className="flex gap-2 items-center">
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs">
            {[
              { key: 'all',    label: 'All' },
              { key: 'unread', label: 'Unread' },
              { key: 'read',   label: 'Read' },
            ].map((f, i) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 font-semibold transition ${
                  i > 0 ? 'border-l border-gray-200' : ''
                } ${
                  filter === f.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <button
            onClick={load}
            className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            <i className="fas fa-sync-alt"></i>
          </button>

          {canPost && (
            <button
              onClick={() => setComposerOpen(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
            >
              <i className="fas fa-plus mr-1"></i> New Announcement
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="bg-white p-8 rounded shadow text-center text-gray-500">
          Loading announcements…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white p-12 rounded shadow text-center text-gray-400">
          <i className="fas fa-bell-slash text-4xl mb-3"></i>
          <p>
            {filter === 'unread' ? 'No unread announcements.' :
             filter === 'read'   ? 'No read announcements yet.' :
             'No announcements yet.'}
          </p>
          {canPost && (
            <p className="text-xs mt-2">Click "New Announcement" to post one.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(a => {
            const meta = catMeta(a.category);
            const scope = scopeLabel(a);
            const canDelete = role === 'admin' || a.posted_by === user?.id;

            return (
              <div
                key={a.id}
                className={`bg-white p-5 rounded shadow border-l-4 transition ${
                  a.is_read ? 'border-gray-300' : 'border-blue-500'
                }`}
              >
                <div className="flex justify-between items-start gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${meta.color}`}>
                      <i className={`fas ${meta.icon} mr-1`}></i> {meta.label}
                    </span>
                    <span className={`text-xs ${scope.color} font-medium`}>
                      <i className={`fas ${scope.icon} mr-1`}></i> {scope.text}
                    </span>
                    {!a.is_read && (
                      <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                        NEW
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">
                    {a.created_at_display || '—'}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-gray-800 mb-1">{a.title}</h3>
                <p className="text-gray-700 whitespace-pre-line mb-3">{a.message}</p>

                <div className="flex justify-between items-center text-xs text-gray-500 border-t pt-2">
                  <span>
                    <i className="fas fa-user-circle mr-1"></i>
                    Posted by <strong>{a.posted_by_name}</strong>
                    <span className="text-gray-400 ml-1 capitalize">
                      ({a.posted_by_role})
                    </span>
                  </span>
                  <div className="flex gap-2">
                    {!a.is_read && (
                      <button
                        onClick={() => markRead(a.id)}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        <i className="fas fa-check mr-1"></i> Mark as read
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => deleteAnnouncement(a)}
                        className="text-red-500 hover:underline"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {composerOpen && (
        <Composer
          role={role}
          onClose={() => setComposerOpen(false)}
          onPosted={() => { setComposerOpen(false); load(); }}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Composer modal — role-aware scope selection
// ═══════════════════════════════════════════════════════════════════
function Composer({ role, onClose, onPosted }) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [scope, setScope] = useState(
    role === 'teacher' ? 'class' :
    role === 'principal' ? 'campus' :
    'global'
  );
  const [selectedCampusId, setSelectedCampusId] = useState('');
  const [selectedClassIds, setSelectedClassIds] = useState([]);   // multi-select
  const [campuses, setCampuses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isPrincipal = role === 'principal';
  const isAdminLike = role === 'admin' || role === 'superintendent';
  const isTeacher = role === 'teacher';

  // ─── Load campuses + classes once ───
  useEffect(() => {
    if (isAdminLike || isPrincipal) {
      Promise.all([
        api.get('/campuses/').catch(() => ({ data: [] })),
        api.get('/classes/').catch(() => ({ data: [] })),
      ]).then(([c, cl]) => {
        setCampuses(c.data || []);
        setClasses(cl.data || []);
        // Auto-select for Principal (they only have one campus)
        if (isPrincipal && c.data?.length) {
          setSelectedCampusId(String(c.data[0].id));
        }
      });
    }
  }, [role, isAdminLike, isPrincipal]);

  // ─── Submit ───
  const handleSubmit = async () => {
    setError('');

    if (!title.trim() || !message.trim()) {
      setError('Title and message are required');
      return;
    }

    // Validation
    if (scope === 'campus') {
      // Principal → auto-selected; Admin → must have chosen
      if (!selectedCampusId) {
        setError('Please select a campus');
        return;
      }
    }
    if (scope === 'class' && selectedClassIds.length === 0) {
      setError('Please select at least one class');
      return;
    }

    setLoading(true);
    try {
      const base = {
        title: title.trim(),
        message: message.trim(),
        category,
      };

      // ─── MULTI-CLASS: one POST per class ───
      if (scope === 'class' && selectedClassIds.length > 1) {
        await Promise.all(
          selectedClassIds.map((classId) =>
            api.post('/announcements/', {
              ...base,
              scope: 'class',
              scope_id: parseInt(classId),
            })
          )
        );
      } else {
        // Single POST — campus / global / one class
        const payload = {
          ...base,
          scope,
          scope_id:
            scope === 'global'
              ? null
              : scope === 'campus'
                ? parseInt(selectedCampusId)
                : parseInt(selectedClassIds[0]),
        };
        await api.post('/announcements/', payload);
      }

      onPosted();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Scope options (role-aware) ───
  const scopeOptions = [];
  if (isAdminLike) {
    scopeOptions.push({ value: 'global', label: 'Everyone' });
    scopeOptions.push({ value: 'campus', label: 'One campus' });
    scopeOptions.push({ value: 'class', label: 'One or more classes' });
  } else if (isPrincipal) {
    scopeOptions.push({ value: 'campus', label: 'My campus' });
    scopeOptions.push({ value: 'class', label: 'One or more classes in my campus' });
  } else if (isTeacher) {
    scopeOptions.push({ value: 'class', label: 'My class parents' });
  }

  // ─── Toggle a class in the multi-select ───
  const toggleClass = (classId) => {
    const idStr = String(classId);
    setSelectedClassIds((prev) =>
      prev.includes(idStr)
        ? prev.filter((x) => x !== idStr)
        : [...prev, idStr]
    );
  };

  const selectAllClasses = () => setSelectedClassIds(classes.map((c) => String(c.id)));
  const clearAllClasses = () => setSelectedClassIds([]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-bold">New Announcement</h3>
          <button onClick={onClose} className="text-2xl text-gray-400 hover:text-gray-700">×</button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded border border-red-200 text-sm">
              <i className="fas fa-exclamation-circle mr-1"></i> {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
            <input
              className="w-full p-2 border rounded"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Parent-Teacher Meeting"
              maxLength={150}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Message *</label>
            <textarea
              className="w-full p-2 border rounded"
              rows="4"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write the announcement details…"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select
                className="w-full p-2 border rounded"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Target</label>
              <select
                className="w-full p-2 border rounded"
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value);
                  setSelectedClassIds([]);
                  // Re-auto-select campus for principal
                  if (e.target.value === 'campus' && isPrincipal && campuses.length > 0) {
                    setSelectedCampusId(String(campuses[0].id));
                  }
                }}
              >
                {scopeOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ─── CAMPUS selector (only for admin/superintendent) ─── */}
          {scope === 'campus' && isAdminLike && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Campus</label>
              <select
                className="w-full p-2 border rounded"
                value={selectedCampusId}
                onChange={(e) => setSelectedCampusId(e.target.value)}
              >
                <option value="">— Select campus —</option>
                {campuses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* ─── Principal + campus: show which campus (read-only) ─── */}
          {scope === 'campus' && isPrincipal && campuses.length > 0 && (
            <div className="bg-blue-50 text-blue-700 p-2 rounded text-xs">
              <i className="fas fa-info-circle mr-1"></i>
              Posting to <strong>{campuses[0].name}</strong> (your assigned campus)
            </div>
          )}

          {/* ─── MULTI-CLASS selector (principal + admin/super) ─── */}
          {scope === 'class' && !isTeacher && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-600">
                  Classes <span className="text-gray-400">({selectedClassIds.length} selected)</span>
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllClasses}
                    className="text-[11px] text-blue-600 hover:underline font-medium"
                  >
                    Select all
                  </button>
                  {selectedClassIds.length > 0 && (
                    <button
                      type="button"
                      onClick={clearAllClasses}
                      className="text-[11px] text-gray-500 hover:underline font-medium"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="border rounded-lg max-h-56 overflow-y-auto bg-gray-50">
                {classes.length === 0 ? (
                  <p className="p-3 text-xs text-gray-400 text-center">No classes found</p>
                ) : (
                  classes.map((c) => {
                    const idStr = String(c.id);
                    const checked = selectedClassIds.includes(idStr);
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-3 p-2.5 cursor-pointer border-b last:border-b-0 transition ${
                          checked ? 'bg-blue-50' : 'hover:bg-white'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleClass(c.id)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {c.name}
                            {c.section ? ` · ${c.section}` : ''}
                          </p>
                          <p className="text-[10px] text-gray-500 truncate">
                            {c.campus_name || '—'}
                          </p>
                        </div>
                        {checked && (
                          <i className="fas fa-check-circle text-blue-600 text-sm"></i>
                        )}
                      </label>
                    );
                  })
                )}
              </div>

              {selectedClassIds.length > 1 && (
                <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  <i className="fas fa-info-circle mr-1"></i>
                  Will create <strong>{selectedClassIds.length}</strong> separate announcements —
                  one per class.
                </p>
              )}
            </div>
          )}

          {/* ─── Teacher auto-assigned message ─── */}
          {scope === 'class' && isTeacher && (
            <div className="bg-blue-50 text-blue-700 p-2 rounded text-xs">
              <i className="fas fa-info-circle mr-1"></i>
              This will be posted to the parents of your class.
            </div>
          )}

          {/* ─── Principal + campus-info note ─── */}
          {scope === 'campus' && isPrincipal && campuses.length === 0 && (
            <div className="bg-amber-50 text-amber-700 p-2 rounded text-xs">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              You are not assigned to a campus. Contact admin.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading
              ? 'Posting…'
              : scope === 'class' && selectedClassIds.length > 1
                ? `Post to ${selectedClassIds.length} classes`
                : 'Post Announcement'}
          </button>
        </div>
      </div>
    </div>
  );
}